"use client"

import { useState } from "react"
import { Ban, Plus, X, Eye, EyeOff } from "@/lib/icons"
import { Card } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Switch } from "@/components/ui/switch"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { useBlacklistStore } from "@/lib/blacklist-store"
import { useTranslations } from "next-intl"
import { PlatformTagsRow } from "@/components/platform-tags-row"
import { BlacklistGroupsTab } from "@/components/blacklist-groups-tab"

export function BlacklistManager() {
  const tt = useTranslations("blacklistManager")
  const [tagInput, setTagInput] = useState("")
  const [userInput, setUserInput] = useState("")
  const [ruleTagsInput, setRuleTagsInput] = useState("")
  const [ruleUsersInput, setRuleUsersInput] = useState("")

  const {
    blacklistedTags,
    blacklistedUsers,
    rules,
    groups,
    enableBlacklist,
    addTag,
    removeTag,
    addUser,
    removeUser,
    addRule,
    removeRule,
    toggleBlacklist,
  } = useBlacklistStore()

  const handleAddTag = () => {
    if (tagInput.trim()) {
      addTag(tagInput)
      setTagInput("")
    }
  }

  const handleAddUser = () => {
    if (userInput.trim()) {
      addUser(userInput)
      setUserInput("")
    }
  }

  const handleAddRule = () => {
    const tags = ruleTagsInput
      .split(/[,\s]+/g)
      .map((t) => t.trim())
      .filter((t) => t.length > 0)
    const users = ruleUsersInput
      .split(/[,\s]+/g)
      .map((u) => u.trim())
      .filter((u) => u.length > 0)

    if (tags.length === 0 && users.length === 0) return

    addRule({ tags, users })
    setRuleTagsInput("")
    setRuleUsersInput("")
  }

  return (
    <Card className="p-3">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Ban className="h-4 w-4 text-muted-foreground" />
          <h3 className="text-sm font-semibold">{tt("hatedTagsUsers")}</h3>
        </div>
        <div className="flex items-center gap-2">
          <Label htmlFor="enable-blacklist" className="text-sm cursor-pointer">
            {enableBlacklist ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
          </Label>
          <Switch id="enable-blacklist" checked={enableBlacklist} onCheckedChange={toggleBlacklist} />
        </div>
      </div>

      <PlatformTagsRow />

      <Tabs defaultValue="tags" className="space-y-4">
        <TabsList className="grid h-auto w-full grid-cols-2 gap-1 sm:grid-cols-4">
          <TabsTrigger value="tags" className="text-xs">{tt("tagsLength", { length: blacklistedTags.length })}</TabsTrigger>
          <TabsTrigger value="groups" className="text-xs">{tt("groupsLength", { length: groups.length })}</TabsTrigger>
          <TabsTrigger value="users" className="text-xs">{tt("usersLength", { length: blacklistedUsers.length })}</TabsTrigger>
          <TabsTrigger value="rules" className="text-xs">{tt("rulesLength", { length: rules.length })}</TabsTrigger>
        </TabsList>

        <TabsContent value="tags" className="space-y-3">
          <div className="flex gap-2">
            <Input
              placeholder={tt("addTagToHatedList")}
              value={tagInput}
              onChange={(e) => setTagInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleAddTag()}
            />
            <Button onClick={handleAddTag} size="sm">
              <Plus className="h-4 w-4" />
            </Button>
          </div>

          <div className="flex flex-wrap gap-2 max-h-32 overflow-y-auto">
            {blacklistedTags.length > 0 ? (
              blacklistedTags.map((tag) => (
                <Badge key={tag} variant="destructive" className="gap-1 pl-2 pr-1">
                  {tag}
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-4 w-4 p-0 hover:bg-transparent"
                    onClick={() => removeTag(tag)}
                  >
                    <X className="h-3 w-3" />
                  </Button>
                </Badge>
              ))
            ) : (
              <p className="text-sm text-muted-foreground italic">{tt("noHatedTags")}</p>
            )}
          </div>
        </TabsContent>

        <TabsContent value="groups" className="space-y-3">
          <BlacklistGroupsTab />
        </TabsContent>

        <TabsContent value="users" className="space-y-3">
          <div className="flex gap-2">
            <Input
              placeholder={tt("addUserToHatedList")}
              value={userInput}
              onChange={(e) => setUserInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleAddUser()}
            />
            <Button onClick={handleAddUser} size="sm">
              <Plus className="h-4 w-4" />
            </Button>
          </div>

          <div className="flex flex-wrap gap-2 max-h-32 overflow-y-auto">
            {blacklistedUsers.length > 0 ? (
              blacklistedUsers.map((user) => (
                <Badge key={user} variant="destructive" className="gap-1 pl-2 pr-1">
                  {user}
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-4 w-4 p-0 hover:bg-transparent"
                    onClick={() => removeUser(user)}
                  >
                    <X className="h-3 w-3" />
                  </Button>
                </Badge>
              ))
            ) : (
              <p className="text-sm text-muted-foreground italic">{tt("noHatedUsers")}</p>
            )}
          </div>
        </TabsContent>

        <TabsContent value="rules" className="space-y-3">
          <div className="space-y-2">
            <div className="space-y-1.5">
              <Label className="text-xs">{tt("tagsSpaceOrCommaSeparated")}</Label>
              <Input
                placeholder={tt("tagOneTagTwo")}
                value={ruleTagsInput}
                onChange={(e) => setRuleTagsInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleAddRule()}
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">{tt("usersOptional")}</Label>
              <Input
                placeholder={tt("username")}
                value={ruleUsersInput}
                onChange={(e) => setRuleUsersInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleAddRule()}
              />
            </div>
            <Button onClick={handleAddRule} size="sm" className="w-full">
              <Plus className="h-4 w-4 mr-2" />
              {tt("addRule")}
            </Button>
          </div>

          <div className="space-y-2 max-h-40 overflow-y-auto pr-1">
            {rules.length > 0 ? (
              rules.map((rule) => (
                <div key={rule.id} className="flex items-start justify-between gap-2">
                  <div className="flex flex-wrap gap-2">
                    {rule.tags.map((t) => (
                      <Badge key={`${rule.id}-${t}`} variant="destructive">
                        {t}
                      </Badge>
                    ))}
                    {rule.users.map((u) => (
                      <Badge key={`${rule.id}-user-${u}`} variant="destructive">
                        {tt("userU", { u })}
                      </Badge>
                    ))}
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 w-7 p-0"
                    onClick={() => removeRule(rule.id)}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              ))
            ) : (
              <p className="text-sm text-muted-foreground italic">{tt("noRules")}</p>
            )}
          </div>
        </TabsContent>
      </Tabs>
    </Card>
  )
}
